import { useState, useCallback } from 'react';
import { getCampaignId, getToken } from '../api/client.js';

const PROBLEM_TYPES = [
  { value: 'client_absent', label: 'Client absent' },
  { value: 'wrong_address', label: 'Mauvaise adresse' },
  { value: 'refusal', label: 'Refus du client' },
  { value: 'damaged', label: 'Matériel endommagé' },
  { value: 'other', label: 'Autre' },
];

/**
 * Modal for reporting a delivery problem with type, note, and optional photo.
 * @param {{ stopId: string, planId: string, onSubmit: (data: object) => void, onCancel: () => void }} props
 */
export default function ProblemModal({ stopId, planId, onSubmit, onCancel }) {
  const [problemType, setProblemType] = useState('');
  const [note, setNote] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const isValid = problemType && (problemType !== 'other' || note.trim());

  const handlePhotoUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError('');
    try {
      const form = new FormData();
      form.append('photo', file);
      form.append('type', 'proof');

      const campaignId = getCampaignId();
      const response = await fetch(`/api/c/${campaignId}/upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'X-Campaign-Id': campaignId,
        },
        body: form,
      });

      if (!response.ok) throw new Error('Erreur upload');
      const data = await response.json();
      setPhotoUrl(data.url);
    } catch (_uploadErr) {
      setError('Échec upload photo');
    } finally {
      setUploading(false);
    }
  }, []);

  const handleSubmit = useCallback(() => {
    if (!isValid) return;
    onSubmit({
      status: 'problem',
      problemType,
      problemNote: note.trim() || null,
      photoUrl: photoUrl || null,
    });
  }, [isValid, problemType, note, photoUrl, onSubmit]);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
      <div className="bg-white w-full max-w-md rounded-t-2xl sm:rounded-2xl p-5 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-bold">Signaler un problème</h2>
          <button onClick={onCancel} className="text-gray-400 text-xl leading-none">&times;</button>
        </div>

        {/* Problem type selection */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Type de problème</label>
          <div className="grid grid-cols-1 gap-2">
            {PROBLEM_TYPES.map((pt) => (
              <button
                key={pt.value}
                onClick={() => setProblemType(pt.value)}
                className={`text-left px-4 py-3 rounded-xl border text-sm font-medium transition-colors ${
                  problemType === pt.value
                    ? 'border-red-500 bg-red-50 text-red-800'
                    : 'border-gray-200 text-gray-700'
                }`}
              >
                {pt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Note (required if "other") */}
        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">
            Note {problemType === 'other' ? '(obligatoire)' : '(optionnel)'}
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Décrivez le problème..."
            className="w-full border rounded-xl px-4 py-3 h-24 resize-none text-sm"
          />
        </div>

        {/* Optional photo */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Photo (optionnel)</label>
          {photoUrl ? (
            <div className="flex items-center gap-2">
              <span className="text-green-600 text-sm">Photo uploadée</span>
              <button onClick={() => setPhotoUrl('')} className="text-xs text-red-500 underline">
                Supprimer
              </button>
            </div>
          ) : (
            <label className="block w-full border-2 border-dashed border-gray-300 rounded-xl py-4 text-center text-sm text-gray-500 cursor-pointer">
              {uploading ? 'Envoi en cours...' : 'Appuyez pour ajouter une photo'}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoUpload}
                className="hidden"
                disabled={uploading}
              />
            </label>
          )}
        </div>

        {error && <p className="text-red-600 text-sm">{error}</p>}

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <button onClick={onCancel} className="flex-1 border border-gray-300 rounded-xl py-3 font-medium">
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isValid || uploading}
            className="flex-1 bg-red-600 text-white rounded-xl py-3 font-medium disabled:opacity-40"
          >
            Envoyer
          </button>
        </div>
      </div>
    </div>
  );
}
